import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';

export const useDividend = (dividendContract, account) => {
  const [dividendData, setDividendData] = useState({
    claimableAmount: '0',
    totalDistributed: '0',
    availableLiquidity: '0'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState(null);

  const loadDividendData = useCallback(async () => {
    if (!dividendContract || !account) {
      setDividendData({
        claimableAmount: '0',
        totalDistributed: '0',
        availableLiquidity: '0'
      });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check if contract is deployed and accessible
      const code = await dividendContract.provider.getCode(dividendContract.address);
      if (code === '0x') {
        throw new Error('Contract not deployed or invalid address');
      }

      // Use the correct method names from your contract
      const [claimableAmount, liquidityStatus] = await Promise.all([
        dividendContract.getClaimableDividends(account).catch(() => ethers.BigNumber.from(0)),
        dividendContract.getLiquidityStatus().catch(() => ({
          totalBalance: ethers.BigNumber.from(0),
          pendingDividends: ethers.BigNumber.from(0),
          availableLiquidity: ethers.BigNumber.from(0)
        }))
      ]);

      setDividendData({
        claimableAmount: ethers.utils.formatEther(claimableAmount),
        totalDistributed: ethers.utils.formatEther(liquidityStatus.pendingDividends || 0),
        availableLiquidity: ethers.utils.formatEther(liquidityStatus.availableLiquidity || 0)
      });

    } catch (error) {
      console.error('Error loading dividend data:', error);
      // Show user-friendly error for important failures
      if (error.message.includes('network') || error.message.includes('connection')) {
        setError('Network connection error. Please check your connection and try again.');
      }
      // Set defaults for contract call failures
      setDividendData({
        claimableAmount: '0',
        totalDistributed: '0',
        availableLiquidity: '0'
      });
    } finally {
      setIsLoading(false);
    }
  }, [dividendContract, account]);

  useEffect(() => {
    loadDividendData();
    
    if (dividendContract && account) {
      // Refresh data every 15 seconds for better UX
      const interval = setInterval(loadDividendData, 15000);
      return () => clearInterval(interval);
    }
  }, [loadDividendData]);

  const claimDividends = async () => {
    if (!dividendContract || isClaiming) return;

    try {
      setIsClaiming(true);
      setError(null);

      // Check claimable amount first
      const claimableAmount = await dividendContract.getClaimableDividends(account);
      if (claimableAmount.eq(0)) {
        throw new Error('No dividends available to claim');
      }

      // Check if contract has sufficient balance
      const liquidityStatus = await dividendContract.getLiquidityStatus();
      if (liquidityStatus.totalBalance.lt(claimableAmount)) {
        throw new Error('Insufficient contract balance. Please try again later.');
      }

      // Estimate gas for the transaction
      const gasEstimate = await dividendContract.estimateGas.claimDividends();
      const gasLimit = gasEstimate.mul(120).div(100); // Add 20% buffer

      // Execute the claim transaction
      const tx = await dividendContract.claimDividends({
        gasLimit: gasLimit
      });

      // Wait for confirmation
      const receipt = await tx.wait();
      
      if (receipt.status === 1) {
        // Refresh data after successful claim
        await loadDividendData();
      } else {
        throw new Error('Transaction failed');
      }

    } catch (error) {
      console.error('Error claiming dividends:', error);
      
      // Parse and display user-friendly error messages
      let errorMessage = 'Failed to claim dividends';
      
      if (error.message.includes('user rejected')) {
        errorMessage = 'Transaction rejected by user';
      } else if (error.message.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds for gas fee';
      } else if (error.message.includes('No dividends to claim')) {
        errorMessage = 'No dividends available to claim';
      } else if (error.message.includes('Insufficient contract balance')) {
        errorMessage = 'Contract has insufficient balance. Please try again later.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.reason) {
        // Extract revert reason from contract
        errorMessage = error.reason;
      }
      
      setError(errorMessage);
      throw error;
    } finally {
      setIsClaiming(false);
    }
  };

  const refreshData = useCallback(() => {
    return loadDividendData();
  }, [loadDividendData]);

  // Listen for contract events if available
  useEffect(() => {
    if (!dividendContract || !account) return;

    const handleDividendClaimed = (player, amount, event) => {
      if (player.toLowerCase() === account.toLowerCase()) {
        console.log('Dividend claimed:', ethers.utils.formatEther(amount), 'BLB');
        // Refresh data after event
        setTimeout(loadDividendData, 2000);
      }
    };

    const handleDividendCalculated = (recipient, amount, cycleId, event) => {
      if (recipient.toLowerCase() === account.toLowerCase()) {
        console.log('New dividend calculated:', ethers.utils.formatEther(amount), 'BLB');
        // Refresh data after event
        setTimeout(loadDividendData, 1000);
      }
    };

    try {
      // Listen for relevant events
      dividendContract.on('DividendClaimed', handleDividendClaimed);
      dividendContract.on('DividendCalculated', handleDividendCalculated);

      return () => {
        dividendContract.off('DividendClaimed', handleDividendClaimed);
        dividendContract.off('DividendCalculated', handleDividendCalculated);
      };
    } catch (error) {
      console.error('Error setting up event listeners:', error);
    }
  }, [dividendContract, account, loadDividendData]);

  return {
    dividendData,
    isLoading,
    isClaiming,
    error,
    claimDividends,
    refreshData
  };
};